import styled from "styled-components";
import { colors } from "../../utils";
import { device } from "../../utils/device";

/* capq-comp-02: the fixed cookie-consent banner (.iyuga-consent-banner,
   position:fixed, bottom:0) sits over whatever occupies the last pixels of
   the viewport. Page is a self-contained height:100vh scroller, so it does
   not inherit any ancestor padding -- its OWN height has to shrink by the
   banner's live-measured --iyuga-consent-reserve (published on :root by
   ConsentContext while the banner shows) so the last row scrolls clear of
   it. Same mechanism SegmentBuilder/style.js and ViewWheel/styles.js already
   use for this bug class. */
export const Page = styled.div`
  height: calc(100vh - var(--iyuga-consent-reserve, 0px));
  overflow-y: auto;
  background-color: ${colors.background};
`;

/* Help-FAB bottom clearance. The Help Assistant FAB is fixed to the bottom-right
   corner (src/components/HelpAssistant: FAB_SIZE 56 + corner inset 24 at >=sm /
   16 below 600px, so its top edge reaches 80px / 72px up from the viewport
   bottom). MEASURED @390 on web-stg 2026-08-15 (no modal open — distinct from
   BUG-explore-398d6d, which only hides the FAB while a MODAL is open): the FAB
   floated over the LAST capture card's Delete / Add-to-Wheel icons. Reserve
   scroll clearance below the list >= the FAB reach + a gap so the last card can
   always be scrolled clear of it. */
export const FAB_CLEARANCE_DESKTOP = 104; // 80px reach + 24px gap
export const FAB_CLEARANCE_MOBILE = 96; //  72px reach + 24px gap

export const Shell = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 760px;
  margin: 0 auto;
  padding: 24px 16px 64px;
  /* Overrides the shorthand bottom above; adds notch safe-area on top. */
  padding-bottom: calc(${FAB_CLEARANCE_DESKTOP}px + env(safe-area-inset-bottom));
  box-sizing: border-box;

  @media (${device.mobilePortrait}) {
    padding: 16px 12px 48px;
    padding-bottom: calc(${FAB_CLEARANCE_MOBILE}px + env(safe-area-inset-bottom));
  }
`;

export const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 20px;
  /* F8 (VISUAL-AUDIT 2026-08-11): at 390px the title + Export-all + New-capture
     row is wider than the viewport, so the "New capture" button clipped off the
     right edge ("+ New ca"). Allow the action group to wrap to its own row
     instead of overflowing. Desktop keeps one row (the items fit, so nothing
     wraps). */
  flex-wrap: wrap;
`;

export const TitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const CountBadge = styled.span`
  min-width: 24px;
  height: 24px;
  padding: 0 8px;
  background-color: ${colors.PrimaryPurple100};
  color: ${colors.PrimaryPurple700};
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
`;

export const AddButtonWrap = styled.div`
  width: 160px;

  @media (${device.mobilePortrait}) {
    width: 120px;
  }
`;

/* Groups the header actions (Export all + New capture) so they sit together on
   the right of the TopBar and wrap cleanly on narrow widths. */
export const TopBarActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;

  /* F8: on phone, once TopBar wraps, take the full second row and keep the
     Export-all + New-capture buttons right-aligned (tidy, fully visible). */
  @media (${device.mobilePortrait}) {
    width: 100%;
    justify-content: flex-end;
  }
`;

export const ExportButtonWrap = styled.div`
  width: 150px;

  @media (${device.mobilePortrait}) {
    width: 128px;
  }
`;

export const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const Card = styled.div`
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  column-gap: 14px;
  row-gap: 6px;
  background-color: ${colors.white};
  border: 1px solid
    ${({ selected }) => (selected ? colors.PrimaryPurple500 : colors.lightBorder)};
  box-shadow: ${({ selected }) =>
    selected ? `0 0 0 1px ${colors.PrimaryPurple500}` : "none"};
  border-radius: 10px;
  padding: 14px 16px;
  transition: box-shadow 0.2s ease, border-color 0.2s ease;

  @media (max-width: 600px) {
    column-gap: 20px;
  }

  &:hover {
    border-color: ${colors.PrimaryPurple300};
    box-shadow: 0 4px 14px rgba(15, 15, 15, 0.06);
  }
`;

export const Thumb = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 8px;
  flex-shrink: 0;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${colors.Highlight};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  img.doc-icon {
    width: 100%;
    height: 100%;
    object-fit: contain;
    padding: 8px;
    box-sizing: border-box;
  }

  /* Kelly (Slack #devs 2026-09-02): a third-party favicon (podcasts.apple.com
     etc.) is typically 16-32px -- we don't control its resolution, so filling
     the 56px tile like doc-icon does forces a visible upscale (blur). Capping
     at half the tile only ever scales DOWN; width/height:auto keeps a
     genuinely small icon at its own true size instead of stretching it up. */
  img.link-favicon {
    width: auto;
    height: auto;
    max-width: 50%;
    max-height: 50%;
    object-fit: contain;
  }

  /* QA-83: the video poster frame fills the thumb exactly like a photo does, so
     a mixed list of photos and videos reads as one grid rather than two. Black
     backdrop because a letterboxed frame's bars should read as film, not as a
     rendering gap. */
  video.video-poster {
    width: 100%;
    height: 100%;
    object-fit: cover;
    background-color: ${colors.black};
    display: block;
    pointer-events: none;
  }
`;

export const CardBody = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const CardMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  width: 100%;
`;

export const CardMetaStart = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  min-width: 0;
  flex: 0 0 auto;
`;

/** Grows on the first row to push the date right; collapses so the date wraps only when it would collide with the chip row. */
export const CardMetaSpacer = styled.span`
  flex: 0 0 0;
  width: 0;
  height: 0;
  margin-left: auto;
  overflow: hidden;
`;

export const CardMetaDate = styled.span`
  font-size: 11px;
  line-height: 1.2;
  color: ${colors.textGrey};
  flex: 0 0 auto;
  white-space: nowrap;
`;

/** The "Notes" glyph trigger next to the type chip — 16px visual icon, 32px
    touch target (project ruling), same invisible centered ::after overlay
    pattern as the row checkboxes / MenuKebabBtn below. */
export const NotesGlyphBtn = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  margin: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  line-height: 0;
  position: relative;

  &::after {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 32px;
    height: 32px;
  }
`;

/** The "Open document" link on a DOC-type capture — visual link (12px text +
    14px icon) unchanged, hit area grown to the 32px floor via the same
    invisible centered ::after overlay used across this file. Shared so the
    inbox card (CaptureCard) and the wheel-attached row (AttachedCaptureRow)
    stay identical instead of drifting copies. */
export const DocLinkAnchor = styled.a`
  font-size: 12px;
  color: ${colors.PrimaryPurple700};
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  width: fit-content;
  position: relative;

  &::after {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 32px;
    height: 32px;
  }
`;

/** A LINK capture's own URL, shown on the card as the payload and openable
    (`capture-link-open`). Same visual link as DocLinkAnchor plus single-line
    truncation, so it inherits the 32px hit area instead of restating it.

    MEASURED 2026-09-09 on web-dev.iyuga.net `main.8c7b3af3.js` and
    web-stg.iyuga.net `main.03b5cb8e.js`: as a raw inline-styled <a> its real
    hit area (elementFromPoint walk out from the centre) was 49x18 at BOTH
    390x844 and 1440x900 — 18px on the short side, under the project's 32px
    click-surface floor. FE #5277 grew the sibling "Open document" anchor and
    the row kebab; these three copies were missed. */
export const CaptureLinkAnchor = styled(DocLinkAnchor)`
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/** The "From <host>" provenance line on an UPLOADED capture
    (`capture-provenance`) — smaller and greyer than a payload link, so it
    carries its own rule rather than extending DocLinkAnchor's 12px purple.

    MEASURED 2026-09-09 on web-dev `main.8c7b3af3.js`: hit area 49x17 at both
    390x844 and 1440x900. */
export const ProvenanceAnchor = styled.a`
  font-size: 11px;
  color: ${colors.textGrey};
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  width: fit-content;
  max-width: 100%;
  position: relative;

  &::after {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 32px;
    height: 32px;
  }
`;

export const TypeChip = styled.span`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  padding: 2px 8px;
  border-radius: 6px;
  background-color: ${colors.Highlight};
  color: ${colors.PrimaryPurple700};
`;

/* Own row at the card's BOTTOM-RIGHT. `flex-basis: 100%` plus Card's wrap
   drops this under the checkbox/thumb/body/kebab row; `justify-content:
   flex-end` pins the chip to the card's right edge (under the kebab). */
export const CardAttachedFooter = styled.div`
  flex: 1 0 100%;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  min-width: 0;
  margin-top: 3px;
`;

export const AttachedChip = styled.span`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  /* Fixed height + horizontal-only padding keeps numeric, alphabetic, and
     long truncated labels optically centered (tall line-boxes push ink up). */
  line-height: 1;
  height: 18px;
  padding: 0 8px;
  box-sizing: border-box;
  border-radius: 6px;
  background-color: #D0FBE7;
  color: #175926;
  max-width: 220px;
  display: inline-flex;
  align-items: center;
  gap: 3px;

  svg {
    display: block;
    flex-shrink: 0;
  }

  @media (max-width: 600px) {
    max-width: 100%;
  }

  &:is(button) {
    appearance: none;
    border: none;
    cursor: pointer;
    font-family: inherit;
  }
`;

/** Label row inside AttachedChip — shared by single- and multi-wheel chips. */
export const AttachedChipLabel = styled.span`
  display: inline-flex;
  align-items: center;
  min-width: 0;
  overflow: hidden;
  text-transform: none;
  line-height: 1;
  gap: 4px;
`;

export const AttachedChipLabelText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1;
`;

export const AttachedChipExtra = styled.span`
  flex-shrink: 0;
  line-height: 1;
`;

/** AttachedChip at its ONE interactive site (CaptureCard renders it
    `as="button"` to reopen the attach picker). AttachedChip itself stays
    untouched because AttachWheelModal renders it as a plain, non-interactive
    <span> inside a clickable wheel row — putting a 32px transparent overlay
    there would sit on top of that row and change where its clicks land.

    MEASURED 2026-09-09 on web-stg.iyuga.net `main.03b5cb8e.js` (the Dev QA
    account has no capture attached to a wheel, so this control does not render
    there — absent is not passing): hit area 49x18 @1440x900 and 49x19
    @390x844, against AttachedChip's fixed `height: 18px`. The visual pill
    height is deliberate (see AttachedChip's optical-centering note) so the
    floor is added as the same invisible centered ::after used across this
    file, not by growing the pill. */
export const AttachedChipButton = styled(AttachedChip)`
  position: relative;

  &::after {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 32px;
    height: 32px;
  }
`;

/** Bulk partial-attach status — yellow pill; separate from AttachedChip (green). */
export const PartialAttachChip = styled.span`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  padding: 2px 8px;
  border-radius: 6px;
  background-color: #FFF2C6;
  color: #AC3800;
  flex-shrink: 0;
  white-space: nowrap;
`;

/* QA-81 (Gordon, 2026-08-04): "tighten the spacing (a little less space between
   them)" — 6px -> 2px between the per-card icons. The 32px hit target on each
   IconBtn is untouched, so the buttons still meet the touch-target ruling; only
   the visual gutter between them closes. */
export const CardActions = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;

  @media (max-width: 600px) {
    margin-top: 4px;
  }
`;

export const IconBtn = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  color: ${colors.darkGrey};
  transition: background-color 0.15s ease, color 0.15s ease;

  &:hover {
    background-color: ${colors.background};
    color: ${colors.PrimaryPurple700};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

/** Compact row action buttons (kebab, detach) — less inner air around the glyph. */
export const MenuKebabBtn = styled(IconBtn)`
  width: ${({ $compact }) => ($compact ? "20px" : "24px")};
  height: ${({ $compact }) => ($compact ? "20px" : "24px")};
  border-radius: 4px;
  position: relative;

  /* 32px minimum touch target (project ruling) WITHOUT growing the compact
     visual box (20px/24px): same invisible, centered ::after overlay pattern
     as the row checkboxes (CbAll / RowCheckbox) below. Covers both the kebab
     trigger ("Actions for ... capture") and the compact Detach button. */
  &::after {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 32px;
    height: 32px;
  }
`;

/* ----- empty / loading / error states ----- */

export const StateWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 12px;
  min-height: 320px;
  padding: 32px 16px;
`;

export const EmptyArt = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background-color: ${colors.Highlight};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.PrimaryPurple600};
`;

export const SkeletonCard = styled.div`
  display: flex;
  gap: 14px;
  align-items: center;
  background-color: ${colors.white};
  border: 1px solid ${colors.lightBorder};
  border-radius: 10px;
  padding: 14px 16px;
`;

export const SkeletonBox = styled.div`
  background: linear-gradient(
    90deg,
    ${colors.disabled} 25%,
    ${colors.background} 37%,
    ${colors.disabled} 63%
  );
  background-size: 400% 100%;
  border-radius: 6px;
  animation: capture-shimmer 1.4s ease infinite;

  @keyframes capture-shimmer {
    0% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0 50%;
    }
  }
`;

export const SentinelRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px 0;
`;

/* ----- composer / modal ----- */

export const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 420px;
  max-width: 100%;
  text-align: left;

  @media (${device.mobilePortrait}) {
    min-width: auto;
    width: 78vw;
  }
`;

/* Gordon priority fix (2026-08-06) — Task 1: the composer's 6 type tabs
   (Text/Photo/Video/Audio/Document/Link) were wrapping to a 2nd row at the
   modal's normal desktop width because the composer forced an unconditional
   inline `flexWrap: "wrap"`. Default here is `nowrap`, with wrap allowed back
   in ONLY under the mobile-portrait breakpoint (a genuinely narrow phone) —
   never at desktop/tablet widths. This is the single shared TypeTabs/TypeTab
   pair (also used by the media source-picker and the attach-wheel tabs), so
   the tightened gap/padding/font-size below apply everywhere it's reused —
   still legible, still clears the 32px touch-target ruling via min-height. */
export const TypeTabs = styled.div`
  display: flex;
  gap: 4px;
  flex-wrap: nowrap;

  @media (${device.mobilePortrait}) {
    flex-wrap: wrap;
  }
`;

export const TypeTab = styled.button`
  /* MEASURED FIX (2026-08-06, adversarial qa-validator on PR #3253): equal-
     width tabs (flex:1, basis 0) forced "Document" into the SAME slot as
     "Text", overflowing its box by a measured 7px at the true floor width
     (ModalBody's 420px min-width) -- real Chromium, DM Sans loaded,
     scrollWidth 72 vs clientWidth 65. Content-hugging (basis: auto) lets each
     tab claim only what its own label needs, so the short labels stop
     wasting room the long one is starved of; flex-grow:1 still lets them
     share out any leftover space evenly (no backticks in this comment on
     purpose -- one inside a styled-components template literal terminates
     it, same footgun BulkBtn's comment already documents below). Re-measured
     after the fix at
     420/480px: 0px overflow on all 6 tabs, every label included. */
  flex: 1 1 auto;
  white-space: nowrap;
  border: 1px solid
    ${({ active }) => (active ? colors.PrimaryPurple500 : colors.lightBorder)};
  background-color: ${({ active }) =>
    active ? colors.PrimaryPurple100 : colors.white};
  color: ${({ active }) =>
    active ? colors.PrimaryPurple700 : colors.darkGrey};
  border-radius: 8px;
  padding: 6px 5px;
  min-height: 32px;
  box-sizing: border-box;
  font-size: 12px;
  font-weight: 600;
  font-family: "DM Sans", sans-serif;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  transition: all 0.15s ease;

  svg {
    font-size: 16px;
    flex-shrink: 0;
  }

  &:hover {
    border-color: ${colors.PrimaryPurple300};
  }
`;

export const Dropzone = styled.label`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 1.5px dashed ${colors.PrimaryPurple300};
  border-radius: 10px;
  background-color: ${colors.Highlight};
  padding: 28px 16px;
  cursor: pointer;
  text-align: center;
  transition: background-color 0.15s ease, border-color 0.15s ease;

  &:hover {
    background-color: ${colors.PrimaryPurple100};
    border-color: ${colors.PrimaryPurple500};
  }
`;

export const PreviewWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  border: 1px solid ${colors.lightBorder};
  border-radius: 10px;
  padding: 10px 12px;
`;

/* ISP-9999 (composer-file-pick-layout-jump) — the empty Dropzone (144px) is
   far taller than the staged PreviewWrap it gets replaced by (78px), and the
   source-toggle row (32px + 16px gap = 48px) vanishes entirely once a file is
   staged. With the dialog vertically centred, that combined ~114px collapse
   re-centres the whole modal, dropping Capture/Cancel under the user's finger
   at the exact moment they reach for them (measured CLS 0.11 @390px — Chrome's
   "poor" threshold is 0.1). `min-height` reserves the empty-state footprint so
   staging a file changes what's drawn, not how tall the slot is. Heights were
   measured live on web-dev.iyuga.net and hold constant from 390px to 1440px
   (this content never wraps or reflows by width).
   `reserveToggle` widens the floor for tabs that render the Upload/Take
   photo/Record toggle (Photo, Video, Audio here) vs. Document, which never
   does.
*/
export const MediaSlot = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-height: ${({ reserveToggle }) => (reserveToggle ? 192 : 144)}px;
`;

export const PreviewThumb = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${colors.Highlight};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  img.doc-icon {
    width: 100%;
    height: 100%;
    object-fit: contain;
    padding: 4px;
    box-sizing: border-box;
  }

  /* Kelly (Slack #devs 2026-09-02): same fix as Thumb above, scaled to this
     smaller 48px preview tile -- cap at half, never upscale a small favicon. */
  img.link-favicon {
    width: auto;
    height: auto;
    max-width: 50%;
    max-height: 50%;
    object-fit: contain;
  }

  /* QA-90: the staged-file glyph follows the capture TYPE (video/audio/doc),
     so a recorded clip never wears a document icon. Line-art, product voice. */
  svg.type-icon {
    width: 26px;
    height: 26px;
    color: ${colors.PrimaryPurple600};
  }
`;

/**
 * QA-96 — the "Uploading…" wait, as the BRAND Wheel loader.
 *
 * Block-centred on both axes inside the modal body (the loader-centred law), so
 * the wait reads as deliberate rather than as a spinner wedged into whichever
 * sub-component happened to own the upload. Shared across every media type,
 * which is the point: the old MUI CircularProgress lived inside the Dropzone and
 * so never appeared for a webcam shot or a recording at all.
 */
export const UploadingOverlay = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 20px 0;
`;

export const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 4px;
`;

/** Side-by-side primary/secondary actions (bulk attach-wheel picker). */
export const BulkWheelModalActions = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 8px;

  & > * {
    flex: 1;
    min-width: 0;
  }
`;

export const BulkSelectionBar = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 4px 4px 8px;
  border-bottom: 1px solid ${colors.lightBorder};
  margin-bottom: 8px;
  width: 100%;
`;

export const BulkSelectionCopy = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const BulkSelectionCancel = styled.button`
  border: none;
  background: transparent;
  color: ${colors.PrimaryPurple500};
  font-family: "DM Sans", sans-serif;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  padding: 4px 0;
  flex-shrink: 0;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const ActionBtnWrap = styled.div`
  width: 120px;
`;

/* ----- attach-wheel list ----- */

export const WheelList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 320px;
  overflow-y: auto;
  padding-right: 4px;
  padding-bottom: 15px;
  /* BUG-capture-787f1c: at 320px this list almost always slices its last row
     mid-content (no fractional-row rounding trick makes an arbitrary pixel
     cap land on a row boundary for every wheel-name/meta length). A hard
     slice with zero fade reads as "the next element below is overlapping
     this list" even when it is a properly separated sibling (see
     AttachHint) -- the eye can't tell "cut off" from "obscured". A bottom
     mask-image fade makes the truncation read as an intentional "more below,
     scroll" affordance instead of a collision. */
  mask-image: linear-gradient(to bottom, #000 calc(100% - 20px), transparent 100%);
  -webkit-mask-image: linear-gradient(to bottom, #000 calc(100% - 20px), transparent 100%);
`;

/* BUG-capture-787f1c: the "Pick a wheel to add to." validation hint used to
   be a bare div relying only on ModalBody's flex `gap` for separation from
   WheelList above it -- visually indistinguishable from "sitting inside" the
   scroll area once WheelList's last row is fade-clipped (see WheelList).
   An explicit top border makes the boundary between "scrollable list" and
   "hint text" a real, always-visible line, independent of flex gap sizing. */
export const AttachHint = styled.div`
  padding: 10px 4px 0;
  border-top: 1px solid ${colors.lightBorder};
`;

export const WheelRow = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  text-align: left;
  border: 1px solid
    ${({ active }) => (active ? colors.PrimaryPurple500 : colors.lightBorder)};
  background-color: ${({ active }) =>
    active ? colors.PrimaryPurple100 : colors.white};
  border-radius: 8px;
  padding: 10px 12px;
  cursor: pointer;
  font-family: "DM Sans", sans-serif;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    border-color: ${colors.PrimaryPurple300};
  }

  &:disabled {
    opacity: 0.8;
    cursor: not-allowed;
  }
`;

export const WheelThumb = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  background-color: ${colors.Highlight};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

/* BUG-capture-f24d6e: search + sort row above the Attach-to-Wheel list. */
export const AttachPickerToolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;

export const AttachSortSelect = styled.select`
  flex-shrink: 0;
  border: 1px solid ${colors.lightBorder};
  background-color: ${colors.white};
  color: ${colors.darkGrey};
  border-radius: 8px;
  height: 40px;
  padding: 0 8px;
  font-family: "DM Sans", sans-serif;
  font-size: 13px;
  cursor: pointer;
`;

/* ----- toolbar: search + type-filter chips + Select (deck 04/14) ----- */

export const Toolbar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 16px;
`;

export const ToolbarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  @media (${device.mobilePortrait}) {
    flex-wrap: wrap;
  }
`;

export const SearchWrap = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid ${colors.lightBorder};
  background-color: ${colors.white};
  border-radius: 8px;
  padding: 0 12px;
  height: 40px;
  transition: border-color 0.15s ease;

  /* QA-80 (Gordon, 2026-08-04 -- universal design law): "the ONLY indicator
     should be a single purple outline around the ENTIRE component's border."
     This wrap is the ONE ring for the whole field, painted with the SAME
     shared recipe (src/styles/focusRing.css's --iy-focus-ring-* tokens)
     every other focusable in the app uses -- not a bespoke border-color tint
     -- so it stays visually and contrast-wise consistent with the rest of
     the design system. The inner <input> below explicitly opts OUT of its
     own separate ring (which src/styles/focusRing.css's doubled-pseudo-class
     :focus-visible:focus-visible rule would otherwise paint directly on it,
     specificity (0,2,0)) so the field never shows two purple outlines again
     -- measured live on web-stg/capture before this fix: an outline directly
     on the <input> (rgb(155,90,248) solid 2px) PLUS this wrap's border
     turning purple, two separate indicators for one field. */
  &:focus-within {
    outline: var(--iy-focus-ring-width) solid var(--iy-focus-ring-color);
    outline-offset: var(--iy-focus-ring-offset);
    box-shadow: 0 0 0 var(--iy-focus-ring-offset) var(--iy-focus-ring-inner);
  }

  svg {
    color: ${colors.darkGrey};
    flex-shrink: 0;
  }
`;

export const SearchInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-family: "DM Sans", sans-serif;
  font-size: 14px;
  color: ${colors.black};

  &::placeholder {
    color: ${colors.textGrey};
  }

  /* Opt OUT of the app-wide focus ring on the input itself -- SearchWrap
     above already paints the ONE ring for the whole component on
     :focus-within. Doubled pseudo-class to match src/styles/focusRing.css's
     own (0,2,0) specificity (its documented technique for outranking a
     plain component-local rule); styled-components injects after the
     static global sheet, so equal specificity resolves by source order in
     this rule's favor -- the same mechanism focusRing.css's own header
     documents for every other opt-out in the app. */
  &:focus-visible:focus-visible {
    outline: none;
    box-shadow: none;
  }
`;

export const SelectBtn = styled.button`
  flex-shrink: 0;
  border: 1px solid
    ${({ active }) => (active ? colors.PrimaryPurple500 : colors.lightBorder)};
  background-color: ${({ active }) =>
    active ? colors.PrimaryPurple100 : colors.white};
  color: ${({ active }) =>
    active ? colors.PrimaryPurple700 : colors.darkGrey};
  border-radius: 8px;
  height: 40px;
  min-width: 84px;
  padding: 0 16px;
  font-family: "DM Sans", sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: ${colors.PrimaryPurple300};
  }
`;

export const Chips = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

export const Chip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid
    ${({ active }) => (active ? colors.PrimaryPurple500 : colors.lightBorder)};
  background-color: ${({ active }) =>
    active ? colors.PrimaryPurple100 : colors.white};
  color: ${({ active }) =>
    active ? colors.PrimaryPurple700 : colors.darkGrey};
  border-radius: 20px;
  height: 32px;
  padding: 0 12px;
  font-family: "DM Sans", sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: ${colors.PrimaryPurple300};
  }
`;

export const ChipCount = styled.span`
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background-color: ${({ active }) =>
    active ? colors.PrimaryPurple500 : colors.background};
  color: ${({ active }) => (active ? colors.white : colors.textGrey)};
  font-size: 11px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

/* Header "select all" checkbox — sits in the chips row, left of "All",
   X-aligned with the per-row checkboxes (deck notes9/10: row checkbox is 17px
   inside the card edge → margin-left:17px; vertically centered with the chips). */
export const CbAll = styled.button`
  margin-left: 17px;
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  border: 2px solid
    ${({ state }) => (state === "none" ? colors.darkGrey : colors.PrimaryPurple500)};
  background-color: ${({ state }) =>
    state === "none" ? colors.white : colors.PrimaryPurple500};
  border-radius: 5px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${colors.white};
  transition: all 0.12s ease;
  padding: 0;
  position: relative;

  /* 32px minimum touch target (project ruling) WITHOUT growing the 22px visual
     box the deck specifies: an invisible, centered overlay extends the hit area
     only. Absolutely positioned, so it changes no layout. */
  &::after {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 32px;
    height: 32px;
  }

  @media (${device.mobilePortrait}) {
    width: 24px;
    height: 24px;
  }
`;

/* ----- per-row select checkbox on a CaptureCard ----- */

/* QA-78 (2) (Gordon, 2026-08-04): "The checkbox belongs in the UPPER-LEFT corner
   of each card, not vertically centered." `align-self: flex-start` pins it to
   the top of the flex row; the 2px nudge lines its 22px box up with the 56px
   thumbnail's top edge rather than sitting a hair above it (Card padding is
   14px 16px, so the checkbox's own top edge is already at the card's content
   top — the nudge is optical alignment against the thumb, measured, not eyeballed). */
export const RowCheckbox = styled.button`
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  align-self: flex-start;
  margin-top: 2px;
  border: 2px solid
    ${({ checked }) => (checked ? colors.PrimaryPurple500 : colors.darkGrey)};
  background-color: ${({ checked }) =>
    checked ? colors.PrimaryPurple500 : colors.white};
  border-radius: 5px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${colors.white};
  transition: all 0.12s ease;
  padding: 0;
  position: relative;

  /* Same 32px hit-area expansion as CbAll — visual box unchanged. */
  &::after {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 32px;
    height: 32px;
  }

  @media (${device.mobilePortrait}) {
    width: 24px;
    height: 24px;
  }
`;

/* ----- bulk-action bar (deck 14, notes9: narrow, centered, content-hugging) ----- */

export const BulkBar = styled.div`
  position: fixed;
  left: 50%;
  bottom: 24px;
  transform: translateX(-50%);
  z-index: 20;
  width: max-content;
  max-width: calc(100vw - 32px);
  display: flex;
  align-items: center;
  gap: 12px;
  background-color: ${colors.white};
  border: 1px solid ${colors.lightBorder};
  border-radius: 12px;
  box-shadow: 0 8px 28px rgba(15, 15, 15, 0.16);
  padding: 10px 14px;

  @media (${device.mobilePortrait}) {
    flex-wrap: wrap;
    justify-content: center;
    bottom: 12px;
  }
`;

export const BulkCount = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: ${colors.primary};
  white-space: nowrap;
`;

export const BulkBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 14px;
  border-radius: 8px;
  font-family: "DM Sans", sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s ease;
  border: 1px solid
    ${({ primary, danger }) =>
      primary
        ? colors.PrimaryPurple500
        : danger
        ? colors.errorTextAA
        : colors.lightBorder};
  background-color: ${({ primary }) =>
    primary ? colors.PrimaryPurple500 : colors.white};
  /* colors.errorTextAA (#C62828), not the raw colors.error token: this is TEXT
     on a white surface, and the theme records that the brighter red fails AA
     there while this darkened step clears it at 5.62:1 and still reads as
     danger. (No backticks in this comment on purpose -- a backtick inside a
     styled-components template literal terminates the literal.) */
  color: ${({ primary, danger }) =>
    primary ? colors.white : danger ? colors.errorTextAA : colors.darkGrey};

  &:hover {
    /* QA-79: the bulk Delete must not read as a peer of the two additive
       actions beside it — it keeps a destructive hue on hover instead of
       inheriting the purple every other bulk button uses. */
    border-color: ${({ danger }) =>
      danger ? colors.errorTextAA : colors.PrimaryPurple500};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }
`;

export const BulkClear = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${colors.darkGrey};

  &:hover {
    background-color: ${colors.background};
    color: ${colors.black};
  }
`;

/* ----- per-card Add-to-Wheel two-action menu (deck 04) ----- */

export const MenuAnchor = styled.div`
  position: relative;
  display: inline-flex;
`;

export const Menu = styled.div`
  position: absolute;
  top: 38px;
  right: 0;
  z-index: 30;
  min-width: 208px;
  background-color: ${colors.white};
  border: 1px solid ${colors.lightBorder};
  border-radius: 10px;
  box-shadow: 0 8px 28px rgba(15, 15, 15, 0.16);
  padding: 6px;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const MenuItemBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  text-align: left;
  border: none;
  background: transparent;
  border-radius: 7px;
  padding: 10px 10px;
  font-family: "DM Sans", sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: ${colors.black};
  cursor: pointer;
  transition: background-color 0.12s ease;

  svg {
    color: ${colors.PrimaryPurple600};
    flex-shrink: 0;
  }

  &:hover {
    background-color: ${colors.Highlight};
  }
`;

/* Respect the reduced-motion preference across every animated surface here. */
export const ReducedMotionGuard = styled.div`
  @media (prefers-reduced-motion: reduce) {
    *,
    *::before,
    *::after {
      animation-duration: 0.001ms !important;
      transition-duration: 0.001ms !important;
    }
  }
`;
